<template>
  <ModalDialog
    ref="dialog"
    title="Edit Stock"
    icon="fa-inventory"
    :submitting="submitting"
    submitTitle="Update"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="stockForm" :labelColumnSize="3">
      <TabbedBox ref="tabBox">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-stock-details">
            <i class="far fa-inventory"></i> Details
          </Tab>
          <Tab tabid="tab-stock-specifications" v-if="!stockTypeIsGlass">
            <i class="far fa-ruler-combined"></i> Specifications
          </Tab>
          <Tab tabid="tab-stock-glass" v-if="stockTypeIsGlass">
            <i class="far fa-layer-group"></i> Glass Details
          </Tab>
        </template>
        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-stock-details">
            <FormRow label="Name"><FormInputText v-model="stock.name" field="name" /></FormRow>
            <FormRow label="Code"><FormInputText v-model="stock.code" field="code" /></FormRow>
            <FormRow label="Stock Category">
              <FormStockCategorySelect placeholder="Category" :stockType="stock.type" v-model="stock.stockCategoryId" field="stockCategoryId" />
            </FormRow>
            
            <FormRow label="Notes"><FormInputTextArea v-model="stock.note" field="note" /></FormRow>

            <FormHorizontalRule />
            <FormRow label="Tracking Quantities"><FormCheckbox v-model="stock.isTrackingQuantity" field="isTrackingQuantity" /></FormRow>
            <FormRow label="Task Completable" v-if="stockTypeIsGeneral" helpTip="Select if the stock can be completed by the task system. Otherwise it will be automatically completed and invoiced on order completion">
              <FormCheckbox v-model="stock.isTaskCompletable" field="isTaskCompletable" />
            </FormRow>
            <FormRow label="Production Completable" v-if="stockTypeIsGeneral" helpTip="Select if the stock can be completed by the production system. Otherwise it is ignored during production processes such as assembly">
              <FormCheckbox v-model="stock.isProductionCompletable" field="isProductionCompletable" />
            </FormRow>
            <FormRow label="Hand Cut Glass" v-if="stockTypeIsGlass">
              <FormCheckbox v-model="stock.isHandCut" field="isHandCut" />
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-stock-specifications" v-if="!stockTypeIsGlass">
            <FormRow label="Unit Of Measure">
              <FormSelect placeholder="Unit of measure" v-model="stock.uomType" field="uomType" :options="stockUOMTypeOptions" />
            </FormRow>

            <div v-show="stockTypeIsGeneral">
              <FormRow label="Weight" helpTip="KG per unit">
                <FormInputText placeholder="Weight" v-model="stock.weight" field="weight" addon="kg" />
              </FormRow>
            </div>

            <div v-show="stockTypeIsGeneral">
              <ContentTitle>Dimensions</ContentTitle>
              <FormRow label="Profile" v-if="uomTypeIsLength">
                <FormSelect placeholder="Profile" v-model="stock.profileType" field="profileType" :options="StockProfileTypeLookup" />
              </FormRow>
              <FormRow label="Width" v-if="uomTypeIsLength || uomTypeIsSquareMetre" helpTip="Width / Thickness of the stock">
                <FormInputText placeholder="Width" v-model="stock.dimWidth" field="dimWidth" addon="mm" />
              </FormRow>
              <FormRow label="Height" v-if="uomTypeIsLength" helpTip="Height of the profile">
                <FormInputText placeholder="Height" v-model="stock.dimHeight" field="dimHeight" addon="mm" />
              </FormRow>

            </div>

          </TabPage>

          <TabPage tabid="tab-stock-glass" v-if="stockTypeIsGlass">
            <FormRow label="Glass Type">
              <FormSelect placeholder="Glass Type" v-model="stock.glassType" field="glassType" :options="glassTypeOptions" />
            </FormRow>
            <FormRow label="Glass Thickness">
              <FormInputText placeholder="Glass Thickness" v-model="stock.thickness" field="thickness" addon="mm" />
            </FormRow>
            <FormHorizontalRule />
            <div class="row">
              <div class="col-6">
                <FormRow label="Two Sided Glass Type" helpTip="Select if the stock has seperate smooth and rough facing sides" :labelColumnSize="6">
                  <FormSelect v-model="stock.faceLayerType" :options="StockFaceLayerTypeLookup" />
                </FormRow>
                <FormRow v-show="stock.isStockUsingFaces" label="Default Side Up" helpTip="Select the default side up for order entry" :labelColumnSize="6">
                  <FormSelect v-model="stock.defaultFaceSideUp" :options="stockFaceUpOptions" field="defaultFaceSideUp" />
                </FormRow>
                <FormRow v-show="stock.isStockUsingFaces" label="Cutting Side Up" helpTip="Select the side up for cutting" :labelColumnSize="6">
                  <FormSelect v-model="stock.cuttingFaceSideUp" :options="stockFaceUpOptions" field="cuttingFaceSideUp" />
                </FormRow>
                <FormRow label="Has Grain Direction" helpTip="Select if the stock has a grain direction. Direction is set per glass size type in stock management" :labelColumnSize="6">
                  <FormCheckbox v-model="stock.isStockUsingGrain" field="isStockUsingGrain" />
                </FormRow>
              </div>
              <div class="col-6">
                <FormRow label="Display Material Type" :labelColumnSize="6" helpTip="How the glass is displayed in the order editor">
                  <FormSelect v-model="stock.stockMaterialType" field="stockMaterialType" :options="StockMaterialTypeLookup" />
                </FormRow>
                <FormRow label="Glass Colour" :labelColumnSize="6" v-if="stock.stockMaterialType == StockMaterialTypes.colour">
                  <FormColourTag v-model="stock.stockColour1" field="stockColour1" />
                </FormRow>
                <FormRow label="Tint Colour" :labelColumnSize="6" v-if="isStockMaterialTypeTexture">
                  <FormColourTag v-model="stock.stockColour1" field="stockColour1" />
                </FormRow>
                <FormRow label="Gradient Primary Colour" :labelColumnSize="6" v-if="stock.stockMaterialType == StockMaterialTypes.gradient">
                  <FormColourTag v-model="stock.stockColour1" field="stockColour1" />
                </FormRow>
                <FormRow label="Gradient Secondary Colour" :labelColumnSize="6" v-if="stock.stockMaterialType == StockMaterialTypes.gradient">
                  <FormColourTag v-model="stock.stockColour2" field="stockColour2" />
                </FormRow>
                <FormRow label="Opacity" :labelColumnSize="6">
                  <FormInputText type="number" addon="%" v-model="stock.stockColourOpacity" field="stockColourOpacity" />
                </FormRow>
              </div>
            </div>
          </TabPage>
        </template>
      </TabbedBox>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'
import FormStockCategorySelect from '@/modules/inventory/components/formStockCategorySelect'
import { InventoryStockTypes, InventoryStockTypeLookup, UOMTypes, UOMTypeLookup, GlassTypes, GlassTypeLookup, StockMaterialTypes, StockMaterialTypeLookup, StockFaceTypes, StockFaceTypeLookup, StockFaceLayerTypes, StockFaceLayerTypeLookup, StockProfileTypeLookup } from '@/store/types'

export default {
  name: 'StockEditDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    FormStockCategorySelect
  },
  props: {
    stockId: Number
  },
  data() {
    return {
      glassTypeOptions: GlassTypeLookup.filter(type => type.value != GlassTypes.all),
      stockFaceUpOptions: StockFaceTypeLookup,

      stock: { },
      stockForm: {},
      submitting: false
    }
  },
  watch: {
    'stock.faceLayerType': function() {
      this.stock.isStockUsingFaces = this.stock.faceLayerType != StockFaceLayerTypes.none
    }
  },
  created() {
    this.StockMaterialTypes = StockMaterialTypes
    this.StockMaterialTypeLookup = StockMaterialTypeLookup
    this.StockFaceLayerTypeLookup = StockFaceLayerTypeLookup
    this.StockProfileTypeLookup = StockProfileTypeLookup
  },
  mounted() {
  },
  computed: {
    stockTypeIsGlass() { return this.stock.type == InventoryStockTypes.glass },
    stockTypeIsGlassService() { return this.stock.type == InventoryStockTypes.glassService },
    stockTypeIsTemplate() { return this.stock.type == InventoryStockTypes.template },
    stockTypeIsGeneral() { return this.stock.type == InventoryStockTypes.general },
    stockTypeIsAssembly() { return this.stock.type == InventoryStockTypes.assembly },

    uomTypeIsSquareMetre() { return this.stock.uomType == UOMTypes.metresq },
    uomTypeIsLength() { return this.stock.uomType == UOMTypes.length },

    stockUOMTypeOptions() {
      // Ignore certain UOM types not applicable to inventory
      var unavailableTypes = [UOMTypes.eachSubItem]
      return UOMTypeLookup.filter(lookup => !unavailableTypes.includes(lookup.value))
    },

    isStockMaterialTypeTexture() {
      return this.stock.stockMaterialType == StockMaterialTypes.reedTexture ||
        this.stock.stockMaterialType == StockMaterialTypes.cathedralTexture ||
        this.stock.stockMaterialType == StockMaterialTypes.satinliteTexture ||
        this.stock.stockMaterialType == StockMaterialTypes.spotswoodTexture
    }
  },
  methods: {
    show(stock) {
      // Reset form
      this.stock = { }
      this.stockForm = {}
      this.submitting = false

      if(stock != null) {
        this.stock = _.cloneDeep(stock)
      }

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var stockData = _.cloneDeep(this.$data.stock)
      
      // Only update certain fields
      var updateData = _.pick(stockData, [
        'name', 'code', 'stockCategoryId', 'uomType', 'note', 'isTrackingQuantity'
      ])
      if(this.stockTypeIsGeneral)
        updateData = _.merge(updateData, _.pick(stockData, ['weight', 'isTaskCompletable', 'isProductionCompletable']))
      if(this.stockTypeIsGeneral && this.uomTypeIsLength)
        updateData = _.merge(updateData, _.pick(stockData, ['dimWidth', 'dimHeight', 'profileType']))
      if(this.stockTypeIsGeneral && this.uomTypeIsSquareMetre)
        updateData = _.merge(updateData, _.pick(stockData, ['dimWidth']))
      if(this.stockTypeIsGlass)
        updateData = _.merge(updateData, _.pick(stockData, ['glassType', 'thickness', 'isHandCut', 'isStockUsingFaces', 'faceLayerType', 'defaultFaceSideUp', 'cuttingFaceSideUp', 'isStockUsingGrain', 'stockMaterialType', 'stockColour1', 'stockColour2', 'stockColourOpacity']))

      this.$apiService.inventoryService.updateStock(this.stockId, updateData)
        .then(() => {
          this.submitting = false
          this.modalClosed(true)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.stockForm = this.processFormResponse(err)
        })
    }
  }
}
</script>